import { useState } from 'react';
import { motion } from 'framer-motion';
import styles from './HeroSection.module.css';

interface HeroSectionProps {
  onScanSubmit: (url: string) => void;
  isScanning: boolean;
}

export function HeroSection({ onScanSubmit, isScanning }: HeroSectionProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a website URL');
      return;
    }

    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'https://' + normalizedUrl;
    }

    try {
      new URL(normalizedUrl);
      onScanSubmit(normalizedUrl);
    } catch {
      setError('Please enter a valid URL');
    }
  };

  return (
    <section className={styles.hero}>
      <div className={styles.container}>
        <motion.div
          className={styles.badge}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <span className={styles.badgeDot} />
          Free security assessment
        </motion.div>

        <motion.h1
          className={styles.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Understand Your Website's{' '}
          <span className={styles.titleAccent}>Risk Profile</span>
        </motion.h1>

        <motion.p
          className={styles.subtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Comprehensive security, performance, and compliance analysis.
          Get clear, actionable insights to protect your online presence.
        </motion.p>

        <motion.form
          className={styles.scanForm}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={styles.urlInput}
              placeholder="Enter your website URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isScanning}
              aria-label="Website URL"
            />
            <motion.button
              type="submit"
              className={`btn btn-primary ${styles.scanButton}`}
              disabled={isScanning}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isScanning ? (
                <>
                  <span className="spinner" />
                  Scanning...
                </>
              ) : 'Scan Now'}
            </motion.button>
          </div>
          {error && (
            <motion.p
              className={styles.error}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.p>
          )}
        </motion.form>

        <motion.p
          className={styles.note}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          Free scan includes 3 of 4 assessment categories. Sign up for complete analysis.
        </motion.p>
      </div>
    </section>
  );
}
